import type * as React from 'react'

import { cn } from '@/shared/lib/cn'
import { Input } from '@/shared/ui/base/input'

type InputWithUnitProps = Omit<React.ComponentProps<typeof Input>, 'id'> & {
  id: string
  /** Единица измерения приставкой справа: «₽», «м²». */
  unit: string
}

/**
 * Поле ввода с единицей измерения справа внутри поля. Единица объявлена пояснением к полю, поэтому
 * программа чтения с экрана называет её вместе со значением, а не отдельным текстом рядом.
 * Переданное пояснение (обычно текст ошибки) не затирается — единица дописывается к нему.
 *
 * Живёт отдельным файлом, а не в `input.tsx`: тот файл принадлежит реестру Basecn и будет перезаписан
 * целиком, стоит добавить любой компонент, который тянет за собой поле ввода.
 */
function InputWithUnit({ id, unit, className, ...props }: InputWithUnitProps) {
  const unitId = `${id}-unit`

  return (
    <div className="relative w-full">
      {/* Правый отступ поля освобождает место под единицу: без него длинное значение уезжало бы под неё. */}
      <Input
        id={id}
        className={cn('pr-9', className)}
        {...props}
        aria-describedby={props['aria-describedby'] ? `${props['aria-describedby']} ${unitId}` : unitId}
      />
      <span
        id={unitId}
        className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center [font-size:var(--control-field-font-size)]"
      >
        {unit}
      </span>
    </div>
  )
}

export { InputWithUnit }
